import { Injectable } from '@angular/core';
import { RestError, ShareServiceClient } from '@azure/storage-file-share';
import { BehaviorSubject, catchError, from, Observable, of, Subject, takeUntil, throwError } from 'rxjs';
import { StorageDataType } from '../models/storage-data-type';
import { AzureTransferProgress } from '../models/storage';
import { ManageStorageLogsService } from './manage-storage-logs.service';
import { EnvironmentService } from './environment.service';
import { ConfirmDialogComponent } from '../../modules/shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { HeaderService } from './header.service';
import { BlobProperties, BlobServiceClient } from '@azure/storage-blob';
import { FileTypes } from '../models/globals';

@Injectable({
  providedIn: 'root',
})
export class StorageDataTransferService {
  // StorageSharedKeyCredential with storage account and account key
  // is only available in Node.js runtime, not in browsers
  // For browsers, for security concerns, please use Shared Access Signature (SAS)
  // or OAuth Token for your authentication.

  private fileSasUrl: string;
  private shareName: string;
  serviceClient;
  shareClient;
  private blobServiceClient: BlobServiceClient;

  constructor(
    private storageLogService: ManageStorageLogsService,
    private env: EnvironmentService,
    private dialog: MatDialog,
    private headerService: HeaderService,
  ) {
    // this.blobServiceClient = new BlobServiceClient(
    //   `https://abmdepsyncstore.blob.core.windows.net/?sv=2025-01-05&spr=https&st=2025-01-17T07%3A55%3A18Z&se=2025-01-18T07%3A55%3A18Z&sr=c&sp=racwdxltfi&sig=ujBD4901GYQc48NXLQCMzgxB6N6t2Fa7hf9Ijy4IEdk%3D`
    // );
  }

  azureUploadRequests = {}; // contains all the existing azure upload requests
  azureDownloadRequests = {}; // contains all the existing azure download requests

  trasferDataDetails: AzureTransferProgress[] = [];
  private AzureTransferData = new BehaviorSubject<AzureTransferProgress[]>(
    this.trasferDataDetails
  );
  public azureTransferData = this.AzureTransferData.asObservable();

  updateAzureArray() {
    this.AzureTransferData.next(this.trasferDataDetails);
  }

  cancelAzureUpload(filename: string, uniqueId?: any) {
    if (this.azureUploadRequests[filename]) {
      this.azureUploadRequests[filename].abort();
      delete this.azureUploadRequests[filename];
      let idx = this.trasferDataDetails
        .map((obj) => obj.id)
        .lastIndexOf(uniqueId);
      if (idx != -1) {
        this.trasferDataDetails[idx].uploaded = ' : Upload Cancelled';
        this.updateAzureArray();
        this.trasferDataDetails.splice(idx, 1);
      }
    }
  }

  cancelAzureDownload(filename: string, uniqueId?: any) {
    if (this.azureDownloadRequests[filename]) {
      this.azureDownloadRequests[filename].abort();
      delete this.azureDownloadRequests[filename];
      let idx = this.trasferDataDetails
        .map((obj) => obj.id)
        .lastIndexOf(uniqueId);
      if (idx != -1) {
        this.trasferDataDetails[idx].uploaded = ' : Transfer Cancelled';
        this.updateAzureArray();
        this.trasferDataDetails.splice(idx, 1);
      }
    }
  }

  cancelAllAzureUpload() {
    for (const k in this.azureUploadRequests) {
      this.azureUploadRequests[k].abort();
      delete this.azureUploadRequests[k];
    }
  }

  enableAzureToShowAtStart() {
    this.trasferDataDetails.forEach((ele) => {
      ele.toShowAtStart = true;
    });
  }

  disableAzureToShowAtStart(name: string) {
    let idx = this.trasferDataDetails.findIndex((log) => log.name == name);
    if (idx != -1) {
      this.trasferDataDetails[idx].toShowAtStart = false;
    }
  }

  makeServiceClientAndShareClientNull() {
    this.serviceClient = null;
    this.shareClient = null;
    this.blobServiceClient = null;
  }

  //#region File Storage APIS
  async azureFilesInitialize(_fileSasUrl: string, _shareName: string) {
    try {
      this.fileSasUrl = _fileSasUrl;
      this.shareName = _shareName;
      // Create a new ShareServiceClient
      this.serviceClient = new ShareServiceClient(this.fileSasUrl);
      // Get ShareClient
      this.shareClient = this.serviceClient.getShareClient(this.shareName);
      this.blobServiceClient = new BlobServiceClient(this.fileSasUrl);
    } catch (Error) {}
  }

  getFileContent(directoryName: string, fileName: string): Observable<string> {
    return from(
      (async () => {
        try {
          directoryName = directoryName.replace(this.env.envVariables.syncStoreRoute + '/', '');
          const shareClient = this.serviceClient.getShareClient(this.shareName);
          const directoryClient = shareClient.getDirectoryClient(directoryName);
          const fileClient = directoryClient.getFileClient(fileName);
  
          const response = await fileClient.download(0, undefined, {
            disableContentMD5Validation: true
          });
  
          if (!response.blobBody) {
            throw new Error('No content in the file');
          }
  
          // Convert blob to text directly
          const blob = await response.blobBody;
          const content = await new Response(blob).text();
          return content;
  
        } catch (error) {
          console.error('Error reading file:', error);
          throw error instanceof Error 
            ? error 
            : new Error('Unknown error while reading file');
        }
      })()
    ).pipe(
      catchError(error => throwError(() => error))
    );
  }

  private async streamToText(readable: NodeJS.ReadableStream): Promise<string> {
    readable.setEncoding('utf8');
    let data = '';
    
    for await (const chunk of readable) {
        data += chunk;
    }
    
    return data;
  }

  async azureFilesCreateDirectory(
    _directoryName: string,
    path: string,
    uniqueId: number
  ) {
    try {
      path = path.replace(this.env.envVariables.syncStoreRoute + '/', '');
      const directoryClient = this.shareClient.getDirectoryClient(
        path + '/' + _directoryName
      );
      await directoryClient.createIfNotExists();
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Completed');
    } catch (Error) {
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Failed');
    }
  }

  azureFilesDownloadFile(
    _fileName: string,
    path: string,
    size: any,
    uniqueId: number
  ): Observable<Blob | null> {
    path = path.replace(this.env.envVariables.syncStoreRoute + '/', '');
    const directoryClient = this.shareClient.getDirectoryClient(path);
    const fileClient = directoryClient.getFileClient(_fileName);
    
    const downloadUrl = fileClient.url;
    
    return new Observable<Blob | null>(observer => {
      const timeStarted = this.headerService.getUserNewDate();
      let cancelled = false;
      let statusUpdated = false;
      const abortController = new AbortController();
      
      const cancellationMechanism = {
        signal: abortController,
        abort: () => {
          cancelled = true;
          abortController.abort();
          
          if (!statusUpdated) {
            statusUpdated = true;
            this.storageLogService.updateTimeRemaining(uniqueId, null);
            this.storageLogService.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
          }
        },
        cancelSubject: new Subject<void>()
      };
      
      this.storageLogService.azureDownloadRequests[uniqueId] = cancellationMechanism;
      
      cancellationMechanism.cancelSubject.subscribe(() => {
        cancellationMechanism.abort();
      });
      
      const cleanup = () => {
        if (this.storageLogService.azureDownloadRequests[uniqueId]) {
          delete this.storageLogService.azureDownloadRequests[uniqueId];
        }
        cancellationMechanism.cancelSubject.complete();
      };
      
      fetch(downloadUrl, {
        method: 'GET',
        signal: abortController.signal
      })
      .then(response => {
        if (cancelled) {
          cleanup();
          observer.next(null); 
          observer.complete();
          return;
        }
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const contentLength = response.headers.get('content-length');
        const total = contentLength ? parseInt(contentLength, 10) : 0;
        
        if (!response.body) {
          throw new Error('ReadableStream not supported');
        }
        
        const reader = response.body.getReader();
        let receivedLength = 0;
        const chunks: Uint8Array[] = [];
        let lastProgress = 0;
        
        const readChunk = (): Promise<void> => {
          return reader.read().then(({ done, value }) => {
            if (cancelled) {
              reader.cancel();
              cleanup();
              observer.next(null); 
              observer.complete();
              return;
            }
            
            if (done) {
              if (!cancelled && !statusUpdated) {
                const blob = new Blob(chunks as BlobPart[]);
                statusUpdated = true;
                this.storageLogService.updateTimeRemaining(uniqueId, null);
                this.storageLogService.updateRequestCompleteFields(uniqueId, 'Transfer Completed');
                cleanup();
                observer.next(blob);
                observer.complete();
              }
              return;
            }
            
            if (value && !cancelled) {
              chunks.push(value);
              receivedLength += value.length;
              
              if (total > 0) {
                const progress = Math.round((receivedLength / total) * 100);
                if (progress !== lastProgress && progress < 100) {
                  lastProgress = progress;
                  
                  const timeElapsed = this.headerService.getUserNewDate().valueOf() - timeStarted.valueOf();
                  const downloadSpeed = receivedLength / (timeElapsed / 1000);
                  const timeRemaining = Math.round((total - receivedLength) / downloadSpeed);
                  
                  this.storageLogService.updateTimeRemaining(uniqueId, isFinite(timeRemaining) ? timeRemaining : 0);
                  this.storageLogService.updateStatusOfLog(uniqueId, `Transferred ${progress}%`);
                  this.storageLogService.updateStorageLogsStore();
                }
              }
            }
            
            if (!cancelled) {
              return readChunk();
            }
          });
        };
        
        return readChunk();
      })
      .catch(error => {
        console.error(`Download error for ${_fileName}:`, error);
        
        if (cancelled || error.name === 'AbortError') {
          if (!statusUpdated) {
            statusUpdated = true;
            this.storageLogService.updateTimeRemaining(uniqueId, null);
            this.storageLogService.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
          }
          cleanup();
          observer.next(null);
          observer.complete();
        } else {
          console.error(`Download failed for ${_fileName}:`, error);
          if (!statusUpdated) {
            statusUpdated = true;
            this.storageLogService.updateTimeRemaining(uniqueId, null);
            this.storageLogService.updateRequestCompleteFields(uniqueId, 'Transfer Failed');
          }
          cleanup();
          observer.error(error);
        }
      });
      
      return cleanup;
    });
  }

  // Parallel uploading a File in browser
  async azureFilesUploadFile(
    _directoryName: string,
    browserFile: File,
    uniqueId: number,
    isEditUpload?: boolean
  ): Promise<void> {
    try {
      _directoryName = _directoryName.replace(this.env.envVariables.syncStoreRoute + '/', '');
      const directoryClient = this.shareClient.getDirectoryClient(_directoryName);
      const fileClient = directoryClient.getFileClient(browserFile.name);

      if (!isEditUpload) {
        let fileExists = await this.azureFilesExists(_directoryName, browserFile.name);
        if (fileExists) {
          const shouldOverwrite = await new Promise<boolean>((resolve) => {
            this.dialog
              .open(ConfirmDialogComponent, {
                autoFocus: false,
                disableClose: false,
                width: '25%',
                data: {
                  titleMessage: 'File already exists !',
                  bodyMessage: `File "${browserFile.name}" already exists, do you want to overwrite the file ?`,
                  cancelText: 'Cancel',
                  submitText: 'Overwrite',
                },
              })
              .afterClosed()
              .subscribe((data) => {
                resolve(!!data);
              });
          });

          if (!shouldOverwrite) {
            this.storageLogService.updateTimeRemaining(uniqueId, null);
            this.storageLogService.updateRequestCompleteFields(
              uniqueId,
              'Cancelled - File already exists'
            );
            return;
          }
        }
      }

      if (browserFile.size === 0) {
        await fileClient.create(0);
        this.storageLogService.updateTimeRemaining(uniqueId, null);
        this.storageLogService.updateRequestCompleteFields(
          uniqueId,
          'Upload Completed'
        );
        return;
      }

      this.storageLogService.createAbortController(uniqueId, true);
      let timeStarted: Date = this.headerService.getUserNewDate();
      let lastProgress = 0;

      await fileClient.uploadData(browserFile, {
        abortSignal: this.storageLogService.azureUploadRequests[uniqueId].signal,
        rangeSize: 4 * 1024 * 1024, // 4MB range size
        concurrency: 20, // 20 concurrency
        onProgress: (ev) => {
          if (browserFile.size === 0) return;
          
          let progress = Math.round((ev.loadedBytes / browserFile.size) * 100);
          let timeElapsed = this.headerService.getUserNewDate().valueOf() - timeStarted.valueOf();
          
          let uploadSpeed = 0;
          let timeRemaining = 0;
          
          if (timeElapsed > 0) {
            uploadSpeed = ev.loadedBytes / (timeElapsed / 1000);
            if (uploadSpeed > 0) {
              timeRemaining = Math.round((browserFile.size - ev.loadedBytes) / uploadSpeed);
            }
          }
          
          if (progress !== lastProgress && progress < 100) {
            lastProgress = progress;
            this.storageLogService.updateTimeRemaining(uniqueId, timeRemaining);
            let ui_progress = 'Uploaded ' + progress + '%';
            this.storageLogService.updateStatusOfLog(uniqueId, ui_progress);
            this.storageLogService.updateStorageLogsStore();
          }
          
        },
      });

      this.storageLogService.updateTimeRemaining(uniqueId, null);
      this.storageLogService.updateRequestCompleteFields(
        uniqueId,
        'Upload Completed'
      );
      this.storageLogService.updateStorageLogsStore();

    } catch (error) {
      console.error('Azure Files upload error:', error);
      
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        this.storageLogService.updateTimeRemaining(uniqueId, null);
        this.storageLogService.updateRequestCompleteFields(
          uniqueId,
          'Upload Cancelled'
        );
      } else {
        this.storageLogService.updateTimeRemaining(uniqueId, null);
        this.storageLogService.updateRequestCompleteFields(
          uniqueId,
          'Upload Failed: ' + (error.message || 'Unknown error')
        );
      }
      this.storageLogService.updateStorageLogsStore();
    }
  }

  async azureJobsUploadFile(
    _directoryName: string,
    browserFile: File,
    uniqueId: number,
    uploadFieldName?: string
  ) {
    try {
      _directoryName = _directoryName.replace(
        this.env.envVariables.syncStoreRoute + '/',
        ''
      );
      const directoryClient = this.shareClient.getDirectoryClient(
        _directoryName.split('@')[0]
      );

      // Check if directory exists
      let directoryExists = await directoryClient
        .getProperties()
        .then(() => {
          return true;
        })
        .catch(async (error) => {
          if (error.statusCode === 404) {
            await directoryClient.create();
            return true;
          } else {
            console.error(error); // Rethrow other errors
            return false;
          }
        });

      const fileClient = directoryClient.getFileClient(browserFile.name);
      let fileExists = await this.azureFilesExists(
        _directoryName,
        browserFile.name
      ).then((data) => {
        if (data == true) return true;
        return false;
      });
      if (fileExists == true) {
        const prom = await new Promise((res, rej) => {
          this.dialog
            .open(ConfirmDialogComponent, {
              autoFocus: false,
              disableClose: false,
              width: '25%',
              data: {
                titleMessage: 'File already exists !',
                bodyMessage: `File "${browserFile.name}" already exists, do you want to overwrite the file ?`,
                cancelText: 'Cancel',
                submitText: 'Overwrite',
              },
            })
            .afterClosed()
            .subscribe((data) => {
              if (!data) {
                res(false);
              }
              res(true);
            });
        }).then((result) => {
          if (!result) return false;
        });
        if (prom == false) {
          this.trasferDataDetails.push({
            id: uniqueId,
            name: browserFile.name,
            uploaded: ' : File already exists.',
            isUploadComplete: true,
            uploadFieldName: uploadFieldName,
          });
          this.updateAzureArray();
          return;
        }
      }
      this.azureUploadRequests[browserFile.name] = new AbortController();
      await fileClient.uploadData(browserFile, {
        abortSignal: this.azureUploadRequests[browserFile.name].signal,
        rangeSize: 4 * 1024 * 1024, // 4MB range size
        concurrency: 20, // 20 concurrency
        onProgress: (ev) => {
          let progress = Math.round((ev.loadedBytes / browserFile.size) * 100);
          let ui_progress = ' : Uploaded ' + progress + '%';
          if (progress == 100) {
            ui_progress = ' : Upload Completed';
          }

          if (this.trasferDataDetails.length == 0) {
            this.trasferDataDetails.push({
              id: uniqueId,
              name: browserFile.name,
              uploaded: ui_progress,
              isUploadComplete: false,
              uploadFieldName: uploadFieldName,
            });
          }

          let idx = this.trasferDataDetails
            .map((obj) => obj.id)
            .lastIndexOf(uniqueId);
          if (idx != -1) {
            this.trasferDataDetails[idx].uploaded = ui_progress;
            if (progress == 100) {
              this.trasferDataDetails[idx].uploaded = ' : Upload Completed';
              this.trasferDataDetails[idx].isUploadComplete = true;
              this.trasferDataDetails[idx].toShowAtStart = false;
              this.updateAzureArray();
              this.trasferDataDetails.splice(idx, 1);
            }
          } else {
            this.trasferDataDetails.push({
              id: uniqueId,
              name: browserFile.name,
              uploaded: ui_progress,
              isUploadComplete: false,
              uploadFieldName: uploadFieldName,
            });
          }
          this.updateAzureArray();
        },
      });
    } catch (Error) {}
  }

  //Create a file and Upload range of bytes to created file
  // async azureFilesUploadFileRange(
  //   _directoryName: string,
  //   _fileName: string,
  //   _content: string
  // ) {
  //   try {
  //     _directoryName = _directoryName.replace(
  //       this.env.envVariables.syncStoreRoute + '/',
  //       ''
  //     );

  //     var Buffer = require('buffer/').Buffer; // note: the trailing slash is important!

  //     // Get its length in bytes.
  //     const contentByteLength = Buffer.byteLength(_content);
  //     const directoryClient =
  //       this.shareClient.getDirectoryClient(_directoryName);
  //     const fileClient = directoryClient.getFileClient(_fileName);

  //     // Create a file
  //     await fileClient.create(contentByteLength);

  //     // Upload range of bytes to created file
  //     await fileClient.uploadRange(_content, 0, contentByteLength);
  //   } catch (Error) {}
  // }

  async azureFilesListFiles(
    _directoryName: string,
    uniqueId?: number,
    cancelContext?: { recentlyCancelled: string[], currentPath: string }
  ): Promise<StorageDataType[]> {
    try {
      _directoryName = _directoryName.replace(
        this.env.envVariables.syncStoreRoute + '/',
        ''
      );
      let syncstoreItems: StorageDataType[] = [];
      let storageDataType = new StorageDataType('', '', '', '', '', null, '');
      const directoryClient =
        this.shareClient.getDirectoryClient(_directoryName);
      // List directories and files
      var dirPath = '';

      let i = 1;
      let iter = directoryClient.listFilesAndDirectories();
      let entity = await iter.next();
      while (!entity.done) {
        if (_directoryName === '.') {
          dirPath = entity.value.name;
        } else {
          dirPath = _directoryName + '/' + entity.value.name;
        }

        if (entity.value.kind === 'directory') {
          storageDataType = new StorageDataType(
            entity.value.name,
            'directory',
            dirPath,
            'NA',
            'NA',
            null,
            'NA'
          );
        } else {
          try {
            let fileClient = directoryClient.getFileClient(entity.value.name);
            let fileProperties = await fileClient.getProperties();

            storageDataType = new StorageDataType(
              entity.value.name,
              'file',
              dirPath,
              'NA',
              'NA',
              fileProperties.lastModified,
              entity.value.properties.contentLength
            );
          } catch (fileError) {
            entity = await iter.next();
            continue;
          }
        }
        syncstoreItems.push(storageDataType);

        entity = await iter.next();
      }
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Completed');
      return syncstoreItems;
    } catch (Error) {
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Failed');

      const isRecentCancellation = cancelContext?.recentlyCancelled?.some(cancelledPath => 
        cancelledPath.includes(_directoryName) || 
        cancelledPath.includes(cancelContext.currentPath)
      );
      
      if (isRecentCancellation) {
        return [];
      }
      
      return undefined;
    }
  }

  async azureFilesDeleteFile(
    _fileName: string,
    path: string,
    uniqueId?: number
  ) {
    try {
      if (path === this.env.envVariables.syncStoreRoute) {
        path = path.replace(this.env.envVariables.syncStoreRoute, '');
      } else {
        path = path.replace(this.env.envVariables.syncStoreRoute + '/', '');
      }

      const directoryClient = this.shareClient.getDirectoryClient(path);
      await directoryClient.deleteFile(_fileName);
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Completed');
    } catch (Error) {
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Failed');
    }
  }

  async azureFilesDeleteDirectory(
    _directoryName: string,
    path: string,
    uniqueId: number
  ) {
    try {
      if (path === this.env.envVariables.syncStoreRoute) {
        path = path.replace(this.env.envVariables.syncStoreRoute, '');
      } else {
        path = path.replace(this.env.envVariables.syncStoreRoute + '/', '');
      }
      const directoryClient = this.shareClient.getDirectoryClient(path);
      await directoryClient.deleteSubdirectory(_directoryName);
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Completed');
    } catch (Error) {
      this.storageLogService.updateStatusOfLog(uniqueId, 'Failed');
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Failed');
    }
  }

  async azureRecursiveFolderDelete(
    _directoryName: string,
    path: string,
    uniqueId: number
  ) {
    try {
      if (path === this.env.envVariables.syncStoreRoute) {
        path = path.replace(this.env.envVariables.syncStoreRoute, '');
      } else {
        path = path.replace(this.env.envVariables.syncStoreRoute + '/', '');
      }
      const pathClient = this.shareClient.getDirectoryClient(path);
      const directoryClient = this.shareClient.getDirectoryClient(
        path + '/' + _directoryName
      );
      await this.recursiveDelete(
        _directoryName,
        path + '/' + _directoryName,
        directoryClient
      );
      await pathClient.deleteSubdirectory(_directoryName);
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Completed');
    } catch (Error) {
      this.storageLogService.updateStatusOfLog(uniqueId, 'Failed');
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Failed');
    }
  }

  async recursiveDelete(_directoryName: string, path: string, client) {
    let iter = client.listFilesAndDirectories();
    let entity = await iter.next();
    while (!entity.done) {
      if (entity.value.kind === 'directory') {
        const done = await this.recursiveDelete(
          entity.value.name,
          path + '/' + entity.value.name,
          this.shareClient.getDirectoryClient(path + '/' + entity.value.name)
        );
        await client.deleteSubdirectory(entity.value.name);
      } else {
        await client.deleteFile(entity.value.name);
      }
      entity = await iter.next();
    }
  }

  async azureFilesRename(path: string, newPath: string, uniqueId: number) {
    try {
      if (path === this.env.envVariables.syncStoreRoute) {
        path = path.replace(this.env.envVariables.syncStoreRoute, '');
      } else {
        path = path.replace(this.env.envVariables.syncStoreRoute + '/', '');
      }
      const directoryClient = this.shareClient.getDirectoryClient(path);
      await directoryClient?.rename(newPath);
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Completed');
    } catch (Error) {
      this.storageLogService.updateStatusOfLog(uniqueId, 'Failed');
      this.storageLogService.updateRequestCompleteFields(uniqueId, 'Failed');
    }
  }

  async azureFilesExists(_directoryName: string, fileName: string) {
    try {
      if (_directoryName === this.env.envVariables.syncStoreRoute) {
        _directoryName = _directoryName.replace(
          this.env.envVariables.syncStoreRoute,
          ''
        );
      } else {
        _directoryName = _directoryName.replace(
          this.env.envVariables.syncStoreRoute + '/',
          ''
        );
      }
      const directoryClient =
        this.shareClient.getDirectoryClient(_directoryName);
      var dirPath = '';
      let i = 1;
      let iter = directoryClient.listFilesAndDirectories();
      let entity = await iter.next();
      while (!entity.done) {
        if (entity.value.kind == 'file') {
          if (entity.value.name == fileName) {
            return true;
          }
        }
        entity = await iter.next();
      }
      return false;
    } catch (error) {}
  }

  // helper function
  // Check if folder exists in given path, if it does not exist then create one
  async verifyDirectoryPath(paths: string[]): Promise<boolean> {
    return new Promise(async (res) => {
      for await (const path of paths) {
        const wait = await this.createDirIfDoesNotExists(path, 1, path);
      }
      res(true);
    });
  }

  async createDirIfDoesNotExists(
    path: string,
    count: number,
    tempPath: string = null
  ): Promise<boolean> {
    try {
      const directoryClient = await this.shareClient.getDirectoryClient(
        tempPath
      );

      const exists = await directoryClient.createIfNotExists();
      if (path == tempPath) {
        return true;
      }
      this.createDirIfDoesNotExists(path, count, path);
    } catch (error: any) {
      if (error instanceof RestError) {
        let newPath = tempPath
          .split('/')
          .reverse()
          ?.splice(count)
          ?.reverse()
          .join('/');
        this.createDirIfDoesNotExists(path, count, newPath);
      }
    }
  }

  async checkDirectoryExistence(path: string) {
    const directoryClient = await this.shareClient.getDirectoryClient(
      path
    );
    let directoryExists = await directoryClient.getProperties().then(async () => {
      const folderName = path.split('/').pop();
      const prom = await new Promise((res, rej) => {
        this.dialog
          .open(ConfirmDialogComponent, {
            autoFocus: false,
            disableClose: false,
            width: '25%',
            data: {
              titleMessage: 'File already exists !',
              bodyMessage: `Folder "${folderName}" already exists at path "${path}", do you want to overwrite the folder ? ( Note: Files/Folders with same name may be affected ).`,
              cancelText: 'Cancel',
              submitText: 'Overwrite',
            },
          })
          .afterClosed()
          .subscribe((data) => {
            if (!data) {
              res(false);
            }
            res(true);
          });
      }).then((result) => {
        return result;
      });
      return prom;
    }).catch(async (error) => {
      return true;
    });
    return directoryExists;
  }
  //#endregion

  //#region Blob Storage APIS
  //#region Create
  createContainer(containerName: string): Observable<any> {
    containerName = this.getUpdatedContainerName(containerName);
    return from(this.createContainerAsync(containerName));
  }

  async createContainerAsync(containerName: string): Promise<any> {
    try {
      const containerClient = this.blobServiceClient.getContainerClient(containerName);
      const createContainerResponse = await containerClient.create();
      return createContainerResponse;
    } catch(e) {
      console.error(e);
      return null;
    }
  }
  //#endregion

  //#region List
  listBlobs(containerName: string): Observable<StorageDataType[]> {
    return from(this.listBlobsAsync(containerName));
  }

  async listBlobsAsync(containerName: string, prefix: string = ""): Promise<StorageDataType[]> {
    try {
      containerName = containerName.replace("./", "");
      containerName = this.getUpdatedContainerName(containerName);
      const baseContainerName = containerName.split('/')[0]; // Get the root container name
      const containerClient = this.blobServiceClient.getContainerClient(baseContainerName);
      
      // If there's a nested path, add it to the prefix
      const nestedPath = containerName.split('/').slice(1).join('/');
      const fullPrefix = nestedPath ? `${nestedPath}/${prefix}` : prefix;
      
      const blobs = containerClient.listBlobsByHierarchy("/", {
        prefix: fullPrefix
      });
      
      let syncstoreItems: StorageDataType[] = [];
      
      for await (const blob of blobs) {
        const type = blob.kind === 'prefix' ? 'directory' : 'file';
        const blobName = blob.kind !== "prefix" 
          ? blob.name.substring(fullPrefix.length) 
          : blob.name.slice(fullPrefix.length, -1);
          
        const createdOn = blob.kind !== "prefix" 
          ? ((blob as any).properties as BlobProperties).createdOn 
          : null;
          
        const contentLength = blob.kind !== "prefix" 
          ? ((blob as any).properties as BlobProperties).contentLength.toString() 
          : "NA";
          
        const blobFullPath = `${containerName}/${blobName}`;
        
        syncstoreItems.push(new StorageDataType(blobName, type, blobFullPath.replace("syncstore/", ""), 'NA', 'NA', createdOn, contentLength));
      }
      return syncstoreItems;
    } catch(e) {
      console.error("Error listing blobs:", e);
      return [];
    }
  }
  //#endregion

  //#region View
  viewBlob(filePath: string): Observable<Blob> {
    filePath = this.getUpdatedContainerName(filePath);
    let containerName = filePath.split("/")[0];
    let blobName = filePath.split("/").slice(1).join("/");
    return from(this.viewBlobAsync(containerName, blobName));
  }

  async viewBlobAsync(containerName: string, blobName: string): Promise<any> {
    const containerClient = this.blobServiceClient.getContainerClient(containerName);
    blobName = decodeURIComponent(blobName);
    const blobClient = containerClient.getBlobClient(blobName);
    
    if(FileTypes.ModelViewerFile === FileTypes.checkFileType(blobName)){
      return blobClient.url;
    }

    // Get blob content from position 0 to the end
    // In browsers, get downloaded data by accessing downloadBlockBlobResponse.blobBody
    const downloadBlockBlobResponse = await blobClient.download();

    if (FileTypes.VideoFile === FileTypes.checkFileType(blobName)) {
      const blob = await downloadBlockBlobResponse.blobBody;
      const url = URL.createObjectURL(blob);
      return url;
    }
    
    // When image file is requested, return blobBody directly
    if(FileTypes.ImageFile == FileTypes.checkFileType(blobName)) {
      return downloadBlockBlobResponse.blobBody;
    }
    const downloaded = await this.blobToString(await downloadBlockBlobResponse.blobBody);
    return downloaded;
  }

  async downloadBlobChunk(containerName: string, blobName: string, start: number, end: number): Promise<ArrayBuffer> {
    const containerClient = this.blobServiceClient.getContainerClient(containerName);
    const blobClient = containerClient.getBlobClient(blobName);
  
    const downloadResponse = await blobClient.download(start, end - start + 1);
  
    const blob = await downloadResponse.blobBody;
    const buffer = await blob.arrayBuffer();
    return buffer;
  }  
 
  //#region Upload
  uploadBlob(containerName: string, file: File, uniqueId): Observable<any> {
    containerName = this.getUpdatedContainerName(containerName);
    return from(this.uploadBlobAsync(containerName, file, uniqueId));
  }

  async uploadBlobAsync(containerName: string, file: File, uniqueId): Promise<any> {
    try {
      const containerClient = this.blobServiceClient.getContainerClient(containerName);
      const blobName = file.name;
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);
      const timeStarted = this.headerService.getUserNewDate();
      let lastProgress = 0;
      
      this.storageLogService.createAbortController(uniqueId, true);
      
      await blockBlobClient.uploadData(file, {
        abortSignal: this.storageLogService.azureUploadRequests[uniqueId].signal,
        blockSize: 4 * 1024 * 1024, // 4MB buffer size instead of default
        concurrency: 20, // 20 concurrency
        onProgress: (ev) => {
          if (file.size === 0) return;
          
          let progress = Math.round((ev.loadedBytes / file.size) * 100);
          let timeElapsed = this.headerService.getUserNewDate().valueOf() - timeStarted.valueOf();
          
          let uploadSpeed = 0;
          let timeRemaining = 0;
          
          if (timeElapsed > 0) {
            uploadSpeed = ev.loadedBytes / (timeElapsed / 1000);
            if (uploadSpeed > 0) {
              timeRemaining = Math.round((file.size - ev.loadedBytes) / uploadSpeed);
            }
          }
          
          if (progress !== lastProgress && progress < 100) {
            lastProgress = progress;
            this.storageLogService.updateTimeRemaining(uniqueId, timeRemaining);
            let ui_progress = 'Uploaded ' + progress + '%';
            this.storageLogService.updateStatusOfLog(uniqueId, ui_progress);
            this.storageLogService.updateStorageLogsStore();
          }
          
        },
      });
      
      this.storageLogService.updateTimeRemaining(uniqueId, null);
      this.storageLogService.updateRequestCompleteFields(
        uniqueId,
        'Upload Completed'
      );
      this.storageLogService.updateStorageLogsStore();
      
      return true;
    } catch (error) {
      console.error('Azure Blob upload error:', error);
      
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        this.storageLogService.updateTimeRemaining(uniqueId, null);
        this.storageLogService.updateRequestCompleteFields(
          uniqueId,
          'Upload Cancelled'
        );
      } else {
        this.storageLogService.updateTimeRemaining(uniqueId, null);
        this.storageLogService.updateRequestCompleteFields(
          uniqueId,
          'Upload Failed: ' + (error.message || 'Unknown error')
        );
      }
      this.storageLogService.updateStorageLogsStore();
      
      throw error;
    }
  }

  async azureBlobJobsUploadFile(
    containerName: string,
    file: File,
    uniqueId: number,
    uploadFieldName?: string
  ) {
    try {
      containerName = this.getUpdatedContainerName(containerName);
      const containerClient = this.blobServiceClient.getContainerClient(containerName);
      const blockBlobClient = containerClient.getBlockBlobClient(file.name);
      this.azureUploadRequests[file.name] = new AbortController();
      await blockBlobClient.uploadData(file, {
        abortSignal: this.azureUploadRequests[file.name].signal,
        blockSize: 4 * 1024 * 1024, // 4MB range size
        concurrency: 20, // 20 concurrency
        onProgress: (ev) => {
          let progress = Math.round((ev.loadedBytes / file.size) * 100);
          let ui_progress = ' : Uploaded ' + progress + '%';
          if (progress == 100) {
            ui_progress = ' : Upload Completed';
          }

          if (this.trasferDataDetails.length == 0) {
            this.trasferDataDetails.push({
              id: uniqueId,
              name: file.name,
              uploaded: ui_progress,
              isUploadComplete: false,
              uploadFieldName: uploadFieldName,
            });
          }

          let idx = this.trasferDataDetails
            .map((obj) => obj.id)
            .lastIndexOf(uniqueId);
          if (idx != -1) {
            this.trasferDataDetails[idx].uploaded = ui_progress;
            if (progress == 100) {
              this.trasferDataDetails[idx].uploaded = ' : Upload Completed';
              this.trasferDataDetails[idx].isUploadComplete = true;
              this.trasferDataDetails[idx].toShowAtStart = false;
              this.updateAzureArray();
              this.trasferDataDetails.splice(idx, 1);
            }
          } else {
            this.trasferDataDetails.push({
              id: uniqueId,
              name: file.name,
              uploaded: ui_progress,
              isUploadComplete: false,
              uploadFieldName: uploadFieldName,
            });
          }
          this.updateAzureArray();
        },
      });
    } catch (Error) {}
  }
  //#endregion

  //#region Delete
  deleteBlob(filePath: string): Observable<any> {
    filePath = this.getUpdatedContainerName(filePath);
    let containerName = filePath.split("/")[0];
    let blobName = filePath.split("/").slice(1).join("/");
    if (filePath.endsWith('/')) {
      return from(this.deleteFolderAsync(containerName, blobName));
    }
    else {
      return from(this.deleteBlobAsync(containerName, blobName));
    }
  }

  async deleteBlobAsync(containerName: string, blobName: string): Promise<any> {
    const containerClient = this.blobServiceClient.getContainerClient(containerName);
    const blobClient = containerClient.getBlobClient(blobName);
    const deleteBlobResponse = await blobClient.delete();
    return deleteBlobResponse;
  }
  //#endregion

  //#region Download
  downloadBlob(filePath: string): Observable<any> {
    filePath = this.getUpdatedContainerName(filePath);
    let containerName = filePath.split("/")[0];
    let blobName = filePath.split("/").slice(1).join("/");
    return from(this.downloadBlobAsync(containerName, blobName));
  }

  async downloadBlobAsync(containerName: string, blobName: string): Promise<any> {
    const containerClient = this.blobServiceClient.getContainerClient(containerName);
    const blobClient = containerClient.getBlockBlobClient(blobName);
    return blobClient.url;
  }
  //#endregion

  getUpdatedContainerName(containerName: string): string {
    let updatedContainerName = containerName.split('/').filter(x => !["syncstore", "mnt"].includes(x)).join('/');
    if(updatedContainerName.charAt(0) == "/"){
      updatedContainerName = updatedContainerName.substring(1);
    }
    let syncstore = "syncstore/";
    if(updatedContainerName == "") {
      syncstore = syncstore.substring(0, syncstore.length - 1);
    }
    return encodeURI(syncstore + updatedContainerName);
  }
  //#endregion

  async deleteFolderAsync(containerName: string, folderPath: string): Promise<void> {
    const containerClient = this.blobServiceClient.getContainerClient(containerName);
    const blobsToDelete: string[] = [];

    // Ensure folderPath ends with '/'
    if (!folderPath.endsWith('/')) {
      folderPath += '/';
    }

    // List blobs that start with the folder path
    for await (const blob of containerClient.listBlobsFlat({ prefix: folderPath })) {
      blobsToDelete.push(blob.name);
    }

    // Delete each blob under the folder
    await Promise.all(
      blobsToDelete.map(async (blobName) => {
        const blobClient = containerClient.getBlobClient(blobName);
        await blobClient.delete();
      })
    );
  }

  //#region Rename
  renameBlob(oldFilePath: string, newFilePath: string): Observable<any> {
    return from(this.renameBlobAsync(oldFilePath, newFilePath));
  }

  async renameBlobAsync(oldFilePath: string, newFilePath: string): Promise<any> {
    try {
      // Handle directory renaming
      if (oldFilePath.endsWith('/')) {
        return await this.renameFolderAsync(oldFilePath, newFilePath);
      }

      // Handle file renaming
      oldFilePath = this.getUpdatedContainerName(oldFilePath);
      newFilePath = this.getUpdatedContainerName(newFilePath);

      let oldContainerName = oldFilePath.split("/")[0];
      let oldBlobName = oldFilePath.split("/").slice(1).join("/");

      let newContainerName = newFilePath.split("/")[0];
      let newBlobName = newFilePath.split("/").slice(1).join("/");

      // Get the original blob
      const oldContainerClient = this.blobServiceClient.getContainerClient(oldContainerName);
      const oldBlobClient = oldContainerClient.getBlobClient(oldBlobName);

      // Download the blob data
      const downloadResponse = await oldBlobClient.download();
      const blobData = await downloadResponse.blobBody;

      // Upload to new location
      const newContainerClient = this.blobServiceClient.getContainerClient(newContainerName);
      const newBlockBlobClient = newContainerClient.getBlockBlobClient(newBlobName);

      await newBlockBlobClient.uploadData(blobData, {
        blockSize: 4 * 1024 * 1024, // 4MB buffer size
        concurrency: 20
      });

      // Delete the original blob
      await oldBlobClient.delete();

      return { success: true, message: 'Blob renamed successfully' };
    } catch (error) {
      console.error('Error renaming blob:', error);
      throw error;
    }
  }

  async renameFolderAsync(oldFolderPath: string, newFolderPath: string): Promise<any> {
    try {
      // Ensure paths end with '/'
      if (!oldFolderPath.endsWith('/')) {
        oldFolderPath += '/';
      }
      if (!newFolderPath.endsWith('/')) {
        newFolderPath += '/';
      }

      oldFolderPath = this.getUpdatedContainerName(oldFolderPath);
      newFolderPath = this.getUpdatedContainerName(newFolderPath);

      let oldContainerName = oldFolderPath.split("/")[0];
      let oldFolderName = oldFolderPath.split("/").slice(1).join("/");

      let newContainerName = newFolderPath.split("/")[0];
      let newFolderName = newFolderPath.split("/").slice(1).join("/");

      const oldContainerClient = this.blobServiceClient.getContainerClient(oldContainerName);
      const newContainerClient = this.blobServiceClient.getContainerClient(newContainerName);

      const blobsToRename: string[] = [];

      // List all blobs in the folder
      for await (const blob of oldContainerClient.listBlobsFlat({ prefix: oldFolderName })) {
        blobsToRename.push(blob.name);
      }

      // Rename each blob in the folder
      await Promise.all(
        blobsToRename.map(async (blobName) => {
          try {
            // Calculate new blob name by replacing the old folder prefix with new folder prefix
            const newBlobName = blobName.replace(oldFolderName, newFolderName);

            // Get the original blob
            const oldBlobClient = oldContainerClient.getBlobClient(blobName);
            const downloadResponse = await oldBlobClient.download();
            const blobData = await downloadResponse.blobBody;

            // Upload to new location
            const newBlobClient = newContainerClient.getBlockBlobClient(newBlobName);
            await newBlobClient.uploadData(blobData, {
              blockSize: 4 * 1024 * 1024,
              concurrency: 20
            });

            // Delete the original blob
            await oldBlobClient.delete();
          } catch (error) {
            console.error(`Error renaming blob ${blobName}:`, error);
            throw error;
          }
        })
      );

      return { success: true, message: 'Folder renamed successfully' };
    } catch (error) {
      console.error('Error renaming folder:', error);
      throw error;
    }
  }
  //#endregion
  //#region Copy
  copyBlob(sourceFilePath: string, destinationFilePath: string): Observable<any> {
    return from(this.copyBlobAsync(sourceFilePath, destinationFilePath));
  }

  async copyBlobAsync(sourceFilePath: string, destinationFilePath: string): Promise<any> {
    try {
      // Handle directory copying
      if (sourceFilePath.endsWith('/')) {
        return await this.copyBlobDirectoryAsync(sourceFilePath, destinationFilePath);
      }

      // Handle file copying
      sourceFilePath = this.getUpdatedContainerName(sourceFilePath);
      destinationFilePath = this.getUpdatedContainerName(destinationFilePath);

      let sourceContainerName = sourceFilePath.split("/")[0];
      let sourceBlobName = sourceFilePath.split("/").slice(1).join("/");

      let destContainerName = destinationFilePath.split("/")[0];
      let destBlobName = destinationFilePath.split("/").slice(1).join("/");

      // Get the source blob
      const sourceContainerClient = this.blobServiceClient.getContainerClient(sourceContainerName);
      const sourceBlobClient = sourceContainerClient.getBlobClient(sourceBlobName);

      // Get the destination blob client
      const destContainerClient = this.blobServiceClient.getContainerClient(destContainerName);
      const destBlobClient = destContainerClient.getBlockBlobClient(destBlobName);

      // Download the source blob
      const downloadResponse = await sourceBlobClient.download();
      const blobData = await downloadResponse.blobBody;

      // Upload to destination
      await destBlobClient.uploadData(blobData, {
        blockSize: 4 * 1024 * 1024, // 4MB buffer size
        concurrency: 20
      });

      return { success: true, message: 'Blob copied successfully' };
    } catch (error) {
      console.error('Error copying blob:', error);
      throw error;
    }
  }

  async copyBlobDirectoryAsync(sourceDirectoryPath: string, destinationDirectoryPath: string): Promise<any> {
    try {
      // Ensure paths end with '/'
      if (!sourceDirectoryPath.endsWith('/')) {
        sourceDirectoryPath += '/';
      }
      if (!destinationDirectoryPath.endsWith('/')) {
        destinationDirectoryPath += '/';
      }

      sourceDirectoryPath = this.getUpdatedContainerName(sourceDirectoryPath);
      destinationDirectoryPath = this.getUpdatedContainerName(destinationDirectoryPath);

      let sourceContainerName = sourceDirectoryPath.split("/")[0];
      let sourceFolderName = sourceDirectoryPath.split("/").slice(1).join("/");

      let destContainerName = destinationDirectoryPath.split("/")[0];
      let destFolderName = destinationDirectoryPath.split("/").slice(1).join("/");

      const sourceContainerClient = this.blobServiceClient.getContainerClient(sourceContainerName);
      const destContainerClient = this.blobServiceClient.getContainerClient(destContainerName);

      const blobsToCopy: string[] = [];

      // List all blobs in the source directory
      for await (const blob of sourceContainerClient.listBlobsFlat({ prefix: sourceFolderName })) {
        blobsToCopy.push(blob.name);
      }

      // Copy each blob to the destination
      const copyPromises = blobsToCopy.map(async (blobName) => {
        try {
          // Calculate destination blob name by replacing source folder prefix with destination folder prefix
          const destBlobName = blobName.replace(sourceFolderName, destFolderName);

          // Get source and destination blob clients
          const sourceBlobClient = sourceContainerClient.getBlobClient(blobName);
          const destBlobClient = destContainerClient.getBlockBlobClient(destBlobName);

          // Download the source blob
          const downloadResponse = await sourceBlobClient.download();
          const blobData = await downloadResponse.blobBody;

          // Upload to destination
          await destBlobClient.uploadData(blobData, {
            blockSize: 4 * 1024 * 1024, // 4MB buffer size
            concurrency: 20
          });
        } catch (error) {
          console.error(`Error copying blob ${blobName}:`, error);
          throw error;
        }
      });

      await Promise.all(copyPromises);

      return { success: true, message: 'Directory copied successfully' };
    } catch (error) {
      console.error('Error copying directory:', error);
      throw error;
    }
  }
  //#endregion

  async blobToString(blob: Blob): Promise<string> {
    const fileReader = new FileReader();
    return new Promise<string>((resolve, reject) => {
      fileReader.onloadend = (ev) => {
        resolve(ev.target?.result as string || '');
      };
      fileReader.onerror = reject;
      fileReader.readAsText(blob);
    });
  }
}
